import { cn } from '../../utils/common-utils.ts';
import ForReviewFlagIcon from '../common/icons/for-review-flag-icon.tsx';

export interface IQuestionData {
  id: number;
  isCompleted: boolean;
  isMarked: boolean;
  isCurrent?: boolean;
}

interface IProps {
  questions: IQuestionData[];
  jumpToQuestion: (id: number) => void;
  onGoToReviewPage?: () => void;
}

const unansweredLegendOutlineStyle = {
  backgroundImage:
    'repeating-linear-gradient(to right, #4B5563 0 3px, transparent 3px 5px), repeating-linear-gradient(to right, #4B5563 0 3px, transparent 3px 5px), repeating-linear-gradient(to bottom, #4B5563 0 3px, transparent 3px 5px), repeating-linear-gradient(to bottom, #4B5563 0 3px, transparent 3px 5px)',
  backgroundPosition: 'top left, bottom left, top left, top right',
  backgroundSize: '100% 1.5px, 100% 1.5px, 1.2px 100%, 1.2px 100%',
  backgroundRepeat: 'no-repeat',
} as const;

const unansweredTileOutlineStyle = {
  backgroundImage:
    'repeating-linear-gradient(to right, #4B5563 0 4px, transparent 4px 6.5px), repeating-linear-gradient(to right, #4B5563 0 4px, transparent 4px 6.5px), repeating-linear-gradient(to bottom, #4B5563 0 4px, transparent 4px 6.5px), repeating-linear-gradient(to bottom, #4B5563 0 4px, transparent 4px 6.5px)',
  backgroundPosition: 'top left, bottom left, top left, top right',
  backgroundSize: '100% 1.5px, 100% 1.5px, 1.2px 100%, 1.2px 100%',
  backgroundRepeat: 'no-repeat',
} as const;

const CurrentQuestionIndicator = ({
  className,
}: {
  className?: string;
}) => (
  <svg
    viewBox="0 0 20 24"
    aria-hidden="true"
    className={cn(className)}
    fill="none"
  >
    <path
      d="M10 22C6.5 17.9 4 15.1 4 10.3C4 6.32 6.69 3 10 3C13.31 3 16 6.32 16 10.3C16 15.1 13.5 17.9 10 22Z"
      fill="white"
      stroke="#111111"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="10" cy="10" r="2.15" fill="#111111" />
  </svg>
);

const QuestionsModal = ({
  questions,
  jumpToQuestion,
  onGoToReviewPage,
}: IProps) => {
  return (
    <div className="relative px-2 pb-[10px] pt-[10px]">
      <div className="mt-1 flex items-center justify-center gap-5 text-[0.85rem] font-medium leading-none text-black">
        <div className="flex items-center gap-1.5">
          <CurrentQuestionIndicator className="h-5 w-5" />
          <span>Current</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="h-[14px] w-[14px] rounded-[2px] bg-white"
            style={unansweredLegendOutlineStyle}
          />
          <span>Unanswered</span>
        </div>
        <div className="flex items-center gap-1.5">
          <ForReviewFlagIcon className="h-[17px] w-[17px]" filled />
          <span>For Review</span>
        </div>
      </div>

      <hr className="my-[14px] border-black/20" />

      <div className="my-[14px] flex flex-wrap justify-center gap-x-5 gap-y-4 pb-[10px] pt-5">
        {questions.map(({ id, isMarked, isCompleted, isCurrent }, index) => {
          return (
            <button
              key={id}
              className={cn(
                'relative flex h-[30px] w-[30px] items-center justify-center text-[1.2rem] font-medium leading-none transition-colors',
                isCompleted
                  ? 'border-2 border-blue bg-blue text-white'
                  : 'bg-white text-blue',
              )}
              onClick={() => {
                jumpToQuestion(id);
              }}
            >
              {!isCompleted && (
                <span
                  className="pointer-events-none absolute inset-0 rounded-[2px]"
                  style={unansweredTileOutlineStyle}
                />
              )}
              {isCurrent && (
                <CurrentQuestionIndicator className="absolute left-1/2 top-[-24px] h-[22px] w-[22px] -translate-x-1/2" />
              )}
              {isMarked && (
                <span className="absolute right-[-8px] top-[-8px] flex h-[16px] w-[16px] items-center justify-center rounded-[4px] bg-white">
                  <ForReviewFlagIcon className="h-[12px] w-[12px]" filled />
                </span>
              )}
              {index + 1}
            </button>
          );
        })}
      </div>

      {onGoToReviewPage && (
        <div className="mt-[14px] flex justify-center">
          <button
            type="button"
            onClick={onGoToReviewPage}
            className="inline-flex items-center rounded-full border-2 border-blue px-5 py-1 text-[0.95rem] font-semibold text-blue transition-colors hover:bg-[#EEF3FF]"
          >
            Go to Review Page
          </button>
        </div>
      )}

      {onGoToReviewPage && (
        <div className="pointer-events-none absolute left-1/2 top-[calc(100%-1px)] -translate-x-1/2">
          <div className="h-0 w-0 border-l-[18px] border-r-[18px] border-t-[16px] border-l-transparent border-r-transparent border-t-white drop-shadow-[0_3px_6px_rgba(15,23,42,0.12)]" />
        </div>
      )}
    </div>
  );
};

export default QuestionsModal;
